import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { casesAPI, clientsAPI, employeesAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { Plus, Search, Edit2, Trash2, Eye, Filter } from 'lucide-react';
import { toast } from 'react-toastify';

const EMPTY_FORM = {
  client: '', assigned_attorney: '', case_title: '', case_number: '',
  case_type: 'Civil', status: 'Open', opened_date: new Date().toISOString().slice(0, 10),
  case_description: '', court_name: '', opposing_party: '', conflict_check_status: 'Pending',
};

const STATUS_TABS = ['All', 'Open', 'Pending', 'On Hold', 'Closed'];

const STATUS_TAB_COLOURS = {
  All:      'border-blue-600 text-blue-700',
  Open:     'border-green-600 text-green-700',
  Pending:  'border-amber-500 text-amber-700',
  'On Hold':'border-gray-400 text-gray-600',
  Closed:   'border-red-400 text-red-600',
};

export default function Cases() {
  const [allCases,  setAllCases]  = useState([]);   // full unfiltered list
  const [clients,   setClients]   = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [statusTab, setStatusTab] = useState('All'); // tab selection
  const [modalOpen, setModalOpen] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const navigate = useNavigate();

  // Load ALL cases once; filtering is done client-side for instant tab switching
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await casesAPI.list({ page_size: 500 });
      setAllCases(data.results || data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    clientsAPI.list().then(r => setClients(r.data.results || r.data));
    employeesAPI.list().then(r => setAttorneys(r.data.results || r.data));
  }, []);

  // Counts per status for badges
  const counts = STATUS_TABS.reduce((acc, s) => {
    acc[s] = s === 'All'
      ? allCases.length
      : allCases.filter(c => c.status === s).length;
    return acc;
  }, {});

  // Visible rows after tab + search filter
  const visible = allCases.filter(c => {
    const matchStatus = statusTab === 'All' || c.status === statusTab;
    const q = search.toLowerCase();
    const matchSearch = !q || c.case_title?.toLowerCase().includes(q)
      || c.case_number?.toLowerCase().includes(q)
      || c.client_name?.toLowerCase().includes(q)
      || c.attorney_name?.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const openAdd  = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setModalOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      editing ? await casesAPI.update(editing.id, form) : await casesAPI.create(form);
      toast.success(editing ? 'Case updated' : 'Case created');
      setModalOpen(false);
      load();
    } catch { toast.error('Failed to save case'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this case and all associated data?')) return;
    try { await casesAPI.delete(id); toast.success('Case deleted'); load(); }
    catch { toast.error('Cannot delete case'); }
  };

  const f = (name) => ({
    value: form[name] || '',
    onChange: e => setForm(prev => ({ ...prev, [name]: e.target.value })),
  });

  return (
    <div className="p-6">
      <PageHeader
        title="Cases & Matters"
        subtitle={`${visible.length} of ${allCases.length} cases`}
        actions={<button className="btn-primary" onClick={openAdd}><Plus size={16} />New Case</button>}
      />

      {/* ── Status tabs ──────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 mb-4 overflow-hidden">
        <div className="flex border-b border-gray-100 px-4 pt-3 gap-0 overflow-x-auto">
          {STATUS_TABS.map(s => (
            <button
              key={s}
              onClick={() => setStatusTab(s)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors mr-1
                ${statusTab === s
                  ? STATUS_TAB_COLOURS[s] || 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              {s}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-normal
                ${statusTab === s ? 'bg-current/10' : 'bg-gray-100 text-gray-500'}`}
                style={statusTab === s ? { backgroundColor: 'rgba(0,0,0,0.08)' } : {}}>
                {counts[s]}
              </span>
            </button>
          ))}
        </div>

        {/* Search bar inside the same card */}
        <div className="p-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input pl-9 text-sm"
              placeholder="Filter by keyword — case title, number, client, attorney…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Case', 'Client', 'Attorney', 'Type', 'Status', 'Opened', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading...</td></tr>
              ) : visible.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">
                  {search ? `No ${statusTab === 'All' ? '' : statusTab + ' '}cases matching "${search}"` : `No ${statusTab === 'All' ? '' : statusTab + ' '}cases`}
                </td></tr>
              ) : visible.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/cases/${c.id}`)}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{c.case_title}</p>
                    <p className="text-xs text-gray-400">#{c.case_number}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{c.client_name}</td>
                  <td className="px-4 py-3 text-gray-600">{c.attorney_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">{c.case_type}</span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{c.opened_date}</td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" onClick={() => navigate(`/cases/${c.id}`)}><Eye size={15} /></button>
                      <button className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded" onClick={() => openEdit(c)}><Edit2 size={15} /></button>
                      <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" onClick={() => handleDelete(c.id)}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Case' : 'New Case'} size="lg">
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Case Title *</label>
            <input className="input" required {...f('case_title')} />
          </div>
          <div>
            <label className="label">Client *</label>
            <select className="input" required {...f('client')}>
              <option value="">Select client</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Assigned Attorney</label>
            <select className="input" {...f('assigned_attorney')}>
              <option value="">Select attorney</option>
              {attorneys.map(a => <option key={a.id} value={a.id}>{a.full_name} ({a.role})</option>)}
            </select>
          </div>
          <div>
            <label className="label">Case Number</label>
            <input className="input" {...f('case_number')} />
          </div>
          <div>
            <label className="label">Case Type</label>
            <select className="input" {...f('case_type')}>
              {['Criminal','Civil','Corporate','Family','Labour','Conveyancing','Constitutional','Immigration','Intellectual Property','Other'].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input" {...f('status')}>
              {['Open','Pending','On Hold','Closed'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Opened Date</label>
            <input type="date" className="input" {...f('opened_date')} />
          </div>
          <div>
            <label className="label">Court Name</label>
            <input className="input" {...f('court_name')} />
          </div>
          <div>
            <label className="label">Opposing Party</label>
            <input className="input" {...f('opposing_party')} />
          </div>
          <div>
            <label className="label">Conflict Check</label>
            <select className="input" {...f('conflict_check_status')}>
              {['Pending','Cleared','Conflict Found'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">Case Description</label>
            <textarea className="input" rows={3} value={form.case_description || ''} onChange={e => setForm({ ...form, case_description: e.target.value })} />
          </div>
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create Case'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
